import React, { useState } from "react";
import { FormikProps } from "formik";
import { Col, Form, Row, InputGroup, Spinner } from "react-bootstrap";
import { MecordColors } from "Constants/Color";
import Autocomplete from "@mui/material/Autocomplete";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ClearIcon from '@mui/icons-material/Clear';
import { Axios } from "api/axios";

interface EnquiryDetailsSectionProps {
  formik: FormikProps<any>;
  isEdit?: boolean;
  sources?: string[];
  isInitiatedByRequired?: boolean;
  type?: 'product' | 'service';
}

const EnquiryDetailsSection: React.FC<EnquiryDetailsSectionProps> = ({
  formik,
  isEdit = false,
  sources = [],
  isInitiatedByRequired = true,
  type = 'product',
}) => {
  const [isGeneratingUid, setIsGeneratingUid] = useState(false);

  const handleGenerateUid = async () => {
    setIsGeneratingUid(true);
    try {
      const response = await Axios.get('/order/uid-number/generate');
      if (response.data.success && response.data.data) {
        formik.setFieldValue('uid_number', response.data.data);
      }
    } catch (error: any) {
      console.error('Error generating UID number:', error);
    } finally {
      setIsGeneratingUid(false);
    }
  };

  const handleClearUid = () => {
    formik.setFieldValue('uid_number', '');
  };

  const hasUidNumber = formik.values.uid_number && formik.values.uid_number.trim() !== '';
  return (
    <div
      className="p-2 rounded mb-3"
      style={{ backgroundColor: MecordColors?.LightGrayColor }}
    >
      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Enquiry Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="enquiry_date"
              value={formik.values.enquiry_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              // readOnly={isEdit}
              isInvalid={
                !!(formik.errors.enquiry_date && formik.touched.enquiry_date)
              }
            />
            {formik.errors.enquiry_date && formik.touched.enquiry_date && (
              <Form.Control.Feedback type="invalid">
                {formik.errors.enquiry_date as string}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Initiated by {isInitiatedByRequired && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              type="text"
              name="initiated_by"
              placeholder="Please Enter Name"
              value={formik.values?.initiated_by || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                !!(formik.errors.initiated_by && formik.touched.initiated_by)
              }
            />
            {formik.errors.initiated_by && formik.touched.initiated_by && (
              <Form.Control.Feedback type="invalid">
                {formik.errors.initiated_by as string}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Reference Number <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="reference_number"
              placeholder="Enter Reference Number"
              value={formik.values?.reference_number || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                !!(
                  formik.errors.reference_number &&
                  formik.touched.reference_number
                )
              }
              // readOnly={isEdit}
            />
            {formik.errors.reference_number &&
              formik.touched.reference_number && (
                <Form.Control.Feedback type="invalid">
                  {formik.errors.reference_number as string}
                </Form.Control.Feedback>
              )}
          </Form.Group>
        </Col>

        {type === 'service' && (
          <Col md={6}>
            <Form.Group>
              <Form.Label>UID Number</Form.Label>
              <InputGroup style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  name="uid_number"
                  value={formik.values.uid_number || ''}
                  readOnly
                  placeholder="Click icon to generate"
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', paddingRight: '40px' }}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '0',
                    zIndex: 10,
                    cursor: isGeneratingUid ? 'not-allowed' : 'pointer',
                    pointerEvents: isGeneratingUid ? 'none' : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={isGeneratingUid ? undefined : (hasUidNumber ? handleClearUid : handleGenerateUid)}
                >
                  {isGeneratingUid ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      style={{ width: '18px', height: '18px', borderWidth: '2px' }}
                    />
                  ) : hasUidNumber ? (
                    <ClearIcon
                      style={{
                        fontSize: '18px',
                        color: MecordColors.SecondaryButtonTextColor,
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <AutoModeIcon
                      style={{
                        fontSize: '18px',
                        color: MecordColors.PrimaryButtonBGColor,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                </div>
              </InputGroup>
            </Form.Group>
          </Col>
        )}

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Source of Enquiry<span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              options={sources}
              value={formik.values.source_of_enquiry || null}
              onChange={(_, value) => {
                formik.setFieldValue("source_of_enquiry", value || "");
              }}
              onBlur={() => {
                formik.setFieldTouched("source_of_enquiry", true);
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  name="source_of_enquiry"
                  placeholder="Select Source"
                  error={Boolean(
                    formik.touched.source_of_enquiry &&
                      formik.errors.source_of_enquiry
                  )}
                  helperText={
                    formik.touched.source_of_enquiry &&
                    typeof formik.errors.source_of_enquiry === "string"
                      ? formik.errors.source_of_enquiry
                      : ""
                  }
                  size="small"
                />
              )}
            />
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              name="description"
              placeholder="Please Enter Description"
              value={formik.values?.description || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default EnquiryDetailsSection;
